import React, { useState, useEffect, useRef, useCallback } from "react";
import { useFormStatus } from "../hooks/useFormStatus";
import { createApplicant } from "../utils/applicants";
import { saveDiscardedForm } from "../utils/discardedForms";
import PublicNav from "../components/PublicNav";
import Footer from "../components/Footer";
import FeedbackModal from "../components/FeedbackModal";
import { useLocation, useNavigate, useBeforeUnload } from "react-router-dom";
 
const Apply = () => {
  const { isOpen, loading, status } = useFormStatus("apply");
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    nsave_id: "",
    age: "",
    skills: "",
    field: "",
    gender: "",
    referral: "",
  });
  const [acceptsReferralTerms, setAcceptsReferralTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackShown, setFeedbackShown] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const [isBackButtonPressed, setIsBackButtonPressed] = useState(false);
  const pageHiddenTimeRef = useRef(null);
  const idleTimerRef = useRef(null);
  const lastInteractionRef = useRef(Date.now());

  // Capture referral from URL on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const refCode = params.get("ref");
    if (refCode) {
      setFormData((prev) => ({
        ...prev,
        referral: refCode,
      }));
    }
  }, [location]);

  // Helper function to check if form has meaningful data (incomplete)
  const hasIncompleteFormData = useCallback(() => {
    if (submitted) return false;
    
    // Check if at least one field is filled
    const hasData = formData.full_name || formData.email || formData.nsave_id || 
                    formData.age || formData.skills || formData.field || formData.gender;
    
    // Check if form is complete
    const isComplete = formData.full_name && formData.email && formData.nsave_id &&
                      formData.age && formData.skills && formData.field && 
                      formData.gender && acceptsReferralTerms;
    
    return hasData && !isComplete;
  }, [formData, acceptsReferralTerms, submitted]);

  // React Router's useBeforeUnload for actual page navigation/refresh
  useBeforeUnload(
    useCallback(
      (e) => {
        if (hasIncompleteFormData() && !feedbackShown) {
          e.preventDefault();
          return (e.returnValue = "لديك بيانات غير محفوظة. هل تريد المغادرة؟");
        }
      },
      [hasIncompleteFormData, feedbackShown]
    )
  );

  // Handle visibility change (tab switching)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is being hidden (user switched tabs)
        pageHiddenTimeRef.current = Date.now();
      } else {
        // Page is visible again
        if (pageHiddenTimeRef.current) {
          const hiddenDuration = Date.now() - pageHiddenTimeRef.current;
          
          // If user was away for more than 3 seconds and has incomplete form
          if (hiddenDuration > 3000 && hasIncompleteFormData() && !feedbackShown) {
            setShowFeedbackModal(true);
            setFeedbackShown(true); // Only show once per session
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hasIncompleteFormData, feedbackShown]);

  // Detect browser back/forward button
  useEffect(() => {
    const handlePopState = (e) => {
      if (hasIncompleteFormData() && !feedbackShown) {
        // Prevent navigation by pushing the current state back
        window.history.pushState(null, '', window.location.href);
        setIsBackButtonPressed(true);
        setShowFeedbackModal(true);
        setFeedbackShown(true);
      }
    };

    // Push current state to enable popstate detection
    window.history.pushState(null, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [hasIncompleteFormData, feedbackShown]);

  // Detect clicks on navigation links
  useEffect(() => {
    const handleLinkClick = (e) => {
      const target = e.target.closest('a');
      if (!target) return;
      
      // Check if it's a navigation link (not external or same page)
      const href = target.getAttribute('href');
      if (href && href.startsWith('/') && href !== location.pathname) {
        if (hasIncompleteFormData() && !feedbackShown) {
          e.preventDefault();
          setPendingNavigation(href);
          setShowFeedbackModal(true);
          setFeedbackShown(true);
        }
      }
    };

    document.addEventListener('click', handleLinkClick, true);
    
    return () => {
      document.removeEventListener('click', handleLinkClick, true);
    };
  }, [hasIncompleteFormData, feedbackShown, location.pathname]);

  // Idle detection - show modal after 30 seconds of inactivity
  useEffect(() => {
    const resetIdleTimer = () => {
      lastInteractionRef.current = Date.now();
      
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }

      // Set timer for 30 seconds of inactivity
      idleTimerRef.current = setTimeout(() => {
        if (hasIncompleteFormData() && !feedbackShown) {
          setShowFeedbackModal(true);
          setFeedbackShown(true);
        }
      }, 30000); // 30 seconds
    };

    // Track user interactions
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      document.addEventListener(event, resetIdleTimer);
    });

    // Start the timer
    resetIdleTimer();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetIdleTimer);
      });
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [hasIncompleteFormData, feedbackShown]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMessage("");

    const result = await createApplicant(formData);
    setMessage(result.message);
    setSubmitting(false);

    if (result.success) {
      setSubmitted(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#05339c]">
        <PublicNav />
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-lg text-white">جاري التحميل...</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!isOpen) {
    return (
      <div className="min-h-screen bg-[#05339c]">
        <PublicNav />
        <div className="flex justify-center items-center min-h-[80vh]">
          <div className="text-center bg-dark-blue p-8 rounded-2xl shadow-2xl max-w-md mx-4 border-2 border-yellow">
            <h1 className="text-2xl font-semibold text-yellow mb-4">
              فترة التسجيل مغلقة
            </h1>
            <p className="text-white">حالة النموذج: {status}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Success page after submission
  if (submitted) {
    return (
      <div className="min-h-screen bg-[#05339c]">
        <PublicNav />
        <div className="flex justify-center items-center min-h-[80vh] p-6">
          <div className="bg-dark-blue rounded-2xl shadow-2xl p-12 border-2 border-green-500 max-w-md w-full text-center">
            <div className="mb-6 flex justify-center">
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center border-4 border-green-500">
                <svg
                  className="w-12 h-12 text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
            <h2 className="text-3xl font-bold text-green-400 mb-4">
              تم التسجيل بنجاح!
            </h2>
            <p className="text-white text-lg ">{message}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05339c]">
      <PublicNav />
      <div className="max-w-2xl mx-auto p-6 py-32">
        <div className="bg-dark-blue rounded-2xl shadow-2xl p-8 border-2 border-yellow">
          <h1 className="text-3xl font-semibold text-center mb-8 text-white">
            تسجيل في <span className="text-yellow">هاكاثون قمة</span>
          </h1>

          {message && !submitted && (
            <div className="mb-6 p-4 rounded-lg flex items-start gap-3 bg-red-500/20 border-2 border-red-500 text-red-400">
              <span className="flex-1">{message}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-yellow mb-2">
                الاسم الثنائي *
              </label>
              <input
dir="ltr"                type="text"
                name="full_name"
                value={formData.full_name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 bg-blue/20 border border-yellow rounded-md focus:outline-none focus:ring-2 focus:ring-yellow text-white placeholder-gray-400"
                placeholder="الاسم الأول واسم العائلة"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-yellow mb-2">
                البريد الإلكتروني *
              </label>
              <input
dir="ltr"                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 bg-blue/20 border border-yellow rounded-md focus:outline-none focus:ring-2 focus:ring-yellow text-white placeholder-gray-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-yellow mb-2">
                ايدي nsave (رقم الجوال المسجل) *
              </label>
              <input
dir="ltr"                type="text"
                name="nsave_id"
                value={formData.nsave_id}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 bg-blue/20 border border-yellow rounded-md focus:outline-none focus:ring-2 focus:ring-yellow text-white placeholder-gray-400"
                placeholder="ادخل ايدي nsave الخاص بك (رقم الجوال المسجل)"
              />
              <p className="text-sm text-yellow mt-2">
                ليس لديك حساب؟ يمكنك التسجيل من{" "}
                <a
                  href="https://web.nsave.com/referral/egyptianfreelancers"
                  className="text-yellow underline font-bold"
                >
                  هنا
                </a>
                ، مع العلم أن المشاركة مقبولة فقط للحسابات المسجلة عبر هذا
                الرابط.{" "}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-yellow mb-2">
                العمر *
              </label>
              <input
dir="ltr"                type="number"
                name="age"
                value={formData.age}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 bg-blue/20 border border-yellow rounded-md focus:outline-none focus:ring-2 focus:ring-yellow text-white placeholder-gray-400"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-yellow mb-2">
                المهارات *
              </label>
              <textarea
                name="skills"
                value={formData.skills}
                onChange={handleInputChange}
                required
                rows="3"
                className="w-full px-3 py-2 bg-blue/20 border border-yellow rounded-md focus:outline-none focus:ring-2 focus:ring-yellow text-white placeholder-gray-400"
                placeholder="اذكر مهاراتك التقنية..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-yellow mb-2">
                المجال *
              </label>
              <select
                name="field"
                value={formData.field}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 bg-blue/20 border border-yellow rounded-md focus:outline-none focus:ring-2 focus:ring-yellow text-white placeholder-gray-400"
              >
                <option value="">اختر المجال</option>
                <option value="تطوير الويب">تطوير الويب</option>
                <option value="تطوير التطبيقات">تطوير التطبيقات</option>
                <option value="الذكاء الاصطناعي">الذكاء الاصطناعي</option>
                <option value="الأمن السيبراني">الأمن السيبراني</option>
                <option value="تحليل البيانات">تحليل البيانات</option>
                <option value="أخرى">أخرى</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-yellow mb-2">
                الجنس *
              </label>
              <div className="flex gap-4 text-white">
                <label className="inline-flex items-center cursor-pointer">
                  <input
dir="ltr"                    type="radio"
                    name="gender"
                    value="ذكر"
                    checked={formData.gender === "ذكر"}
                    onChange={handleInputChange}
                    required
                    className="ml-2 w-5 h-5 appearance-none rounded-full border-2 border-yellow bg-transparent cursor-pointer checked:bg-yellow checked:border-yellow relative before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:w-2 before:h-2 before:rounded-full before:bg-dark-blue checked:before:block before:hidden transition-all duration-200"
                  />
                  <span>ذكر</span>
                </label>
                <label className="inline-flex items-center cursor-pointer">
                  <input
dir="ltr"                    type="radio"
                    name="gender"
                    value="أنثى"
                    checked={formData.gender === "أنثى"}
                    onChange={handleInputChange}
                    required
                    className="ml-2 w-5 h-5 appearance-none rounded-full border-2 border-yellow bg-transparent cursor-pointer checked:bg-yellow checked:border-yellow relative before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2 before:w-2 before:h-2 before:rounded-full before:bg-dark-blue checked:before:block before:hidden transition-all duration-200"
                  />
                  <span>أنثى</span>
                </label>
              </div>
            </div>

            {formData.referral && (
              <div className="bg-yellow/10 border-2 border-yellow rounded-lg p-4">
                <p className="text-sm text-yellow">
                  <span className="font-semibold">كود الإحالة:</span>{" "}
                  {formData.referral}
                </p>
              </div>
            )}

            <div className="flex items-start gap-3 p-4 bg-yellow/10 border-2 border-yellow rounded-lg">
              <input
dir="ltr"                type="checkbox"
                id="referralTerms"
                checked={acceptsReferralTerms}
                onChange={(e) => setAcceptsReferralTerms(e.target.checked)}
                required
                className="mt-1 w-5 h-5 appearance-none rounded border-2 border-yellow bg-transparent cursor-pointer checked:bg-yellow checked:border-yellow relative flex-shrink-0 after:content-['✓'] after:absolute after:top-1/2 after:left-1/2 after:-translate-x-1/2 after:-translate-y-1/2 after:text-dark-blue after:font-bold after:text-sm checked:after:block after:hidden transition-all duration-200"
              />
              <label
                htmlFor="referralTerms"
                className="text-white text-sm cursor-pointer"
              >
                أؤكد تسجيلي في nsave عبر{" "}
                <a
                  href="https://web.nsave.com/referral/egyptianfreelancers"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-yellow underline font-bold hover:text-yellow/80 transition-colors"
                >
                  رابط الإحالة الخاص بالهاكاثون
                </a>{" "}
                *
              </label>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-yellow text-dark-blue py-3 px-4 rounded-md hover:bg-yellow/80 focus:outline-none focus:ring-2 focus:ring-yellow disabled:opacity-50 transition-all duration-300 font-bold shadow-lg"
            >
              {submitting ? "جاري الإرسال..." : "تسجيل"}
            </button>
          </form>
        </div>
      </div>
      <Footer />
      
      {/* Feedback Modal */}
      {showFeedbackModal && (
        <FeedbackModal
          onClose={async () => {
            // Save discarded form data when user abandons the form
            await saveDiscardedForm(formData, acceptsReferralTerms);
            
            setShowFeedbackModal(false);
            // If there's a pending navigation, execute it
            if (pendingNavigation) {
              navigate(pendingNavigation);
              setPendingNavigation(null);
            }
            // If back button was pressed, allow navigation back
            else if (isBackButtonPressed) {
              setIsBackButtonPressed(false);
              window.history.back();
            }
          }}
          userEmail={formData.email}
        />
      )}
    </div>
  );
};

export default Apply;
